/**
 * SHER-803 - the invite link, the one place the root token `S` travels.
 *
 *     https://<tenant-web>/accept-invitation#<invitationUuid>.<S>.<wrapSalt>
 *
 * Everything secret sits after the `#`. Browsers never send the fragment to a server - not in the
 * request line, not in `Referer` - which is why the SHER-769 design puts `S` there (decision doc
 * 3.6; Bitwarden Send does the same). The API only ever learns `authProof`, derived from `S`.
 *
 * The salt rides along so the invitee's browser can derive `authProof` before it has asked the
 * server anything; the salt is public by design and useless without `S`.
 *
 * `S` and the salt are base64url, not base64: `+`, `/` and `=` get rewritten by mail clients,
 * chat apps and URL parsers often enough that a link pasted into WhatsApp could otherwise arrive
 * subtly broken - and an AES-GCM unwrap with a one-bit-different token is indistinguishable from
 * a forged link.
 */

import { ACCEPT_INVITATION_ROUTE } from '../routes/urls';

export const ACCEPT_INVITATION_PATH: string = ACCEPT_INVITATION_ROUTE;

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ROOT_TOKEN_B64URL_CHARS = 22;  // 16 bytes
const MIN_SALT_B64URL_CHARS = 22;    // 16 bytes, the API's floor
const B64URL = /^[A-Za-z0-9_-]+$/;

export interface AcceptFragment {
    invitationId: string;
    /** Standard base64, as `invitationWrap.ts` expects it. */
    rootToken: string;
    /** Standard base64, as `invitationWrap.ts` expects it. */
    wrapSalt: string;
}

function toBase64Url(base64: string): string {
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): string {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    return base64 + '='.repeat((4 - (base64.length % 4)) % 4);
}

export function buildAcceptLink(origin: string, invitationUuid: string, rootToken: string, wrapSalt: string): string {
    return `${origin.replace(/\/+$/, '')}${ACCEPT_INVITATION_PATH}#${invitationUuid}.${toBase64Url(rootToken)}.${toBase64Url(wrapSalt)}`;
}

/** Parse `#<uuid>.<S>.<salt>`. Anything that is not exactly that shape is null - never a guess. */
export function parseAcceptFragment(hash: string): AcceptFragment | null {
    const parts = (hash || '').replace(/^#/, '').split('.');
    if (parts.length !== 3) {
        return null;
    }
    const [invitationId, token, salt] = parts;
    if (!UUID_SHAPE.test(invitationId)
        || !B64URL.test(token) || token.length !== ROOT_TOKEN_B64URL_CHARS
        || !B64URL.test(salt) || salt.length < MIN_SALT_B64URL_CHARS) {
        return null;
    }
    return { invitationId, rootToken: fromBase64Url(token), wrapSalt: fromBase64Url(salt) };
}

/**
 * Read the fragment ONCE and remove it from the address bar before anything else runs.
 *
 * Not tidiness: several error paths in this client report `window.location.href` verbatim
 * (`postErrorSlack` in utils/index.jsx, the auth failure reporter in services/authService.js).
 * A crash on the accept page with the fragment still in place would post `S` to the API and on to
 * Slack. After this call the secret lives only in the caller's memory. A malformed fragment is
 * wiped too - it may still be most of a real token.
 */
export function consumeAcceptFragment(win: Window): AcceptFragment | null {
    const parsed = parseAcceptFragment(win.location.hash);
    if (win.location.hash) {
        win.history.replaceState(win.history.state, '', win.location.pathname + win.location.search);
    }
    return parsed;
}

/**
 * An existing-account invitee who is not signed in has to leave the accept page for the sign-in
 * page and come back - and the fragment was wiped on arrival. The parsed link is parked in
 * sessionStorage for that one round trip: it stays in this tab, is never sent anywhere (no error
 * reporter reads storage), and is removed as soon as the page reads it back or reaches a terminal
 * state. Closing the tab drops it.
 */
const PENDING_KEY = 'sher803.pendingInvitation';

export function stashPendingInvitation(fragment: AcceptFragment, storage: Storage = window.sessionStorage): void {
    storage.setItem(PENDING_KEY, JSON.stringify(fragment));
}

/** Read and remove in one step, so a parked link is used at most once. */
export function takePendingInvitation(storage: Storage = window.sessionStorage): AcceptFragment | null {
    const raw = storage.getItem(PENDING_KEY);
    storage.removeItem(PENDING_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed.invitationId === 'string' && typeof parsed.rootToken === 'string' && typeof parsed.wrapSalt === 'string'
            ? { invitationId: parsed.invitationId, rootToken: parsed.rootToken, wrapSalt: parsed.wrapSalt }
            : null;
    } catch (error) {
        return null;
    }
}

/**
 * Boot-time capture, the FIRST thing the app does (src/bootAcceptFragment.ts is index.jsx's first
 * import). Sentry Replay starts at Sentry.init, before React mounts, and records
 * `window.location.href`; mixpanel and the telemetry module also start before any component's
 * effect. Waiting for the accept page's useEffect would let all of them see the fragment. So on
 * `/accept-invitation` the fragment is parsed and wiped synchronously at module evaluation, and
 * held here until the page takes it.
 */
let bootCapture: AcceptFragment | null = null;

export function captureAcceptFragmentAtBoot(win: Window): void {
    // Normally index.html's first inline script has already stripped the URL and left the raw
    // fragment on this non-enumerable property; take it and delete it at once.
    const holder = win as unknown as { __sher803InviteFragment?: string };
    const stripped = holder.__sher803InviteFragment;
    if (typeof stripped === 'string') {
        delete holder.__sher803InviteFragment;
        bootCapture = parseAcceptFragment(stripped);
        return;
    }
    // Fallback (a page served without that script): strip it here.
    if (win.location.pathname === ACCEPT_INVITATION_PATH && win.location.hash) {
        bootCapture = consumeAcceptFragment(win);
    }
}

/** The link captured at boot, once. */
export function takeBootAcceptFragment(): AcceptFragment | null {
    const captured = bootCapture;
    bootCapture = null;
    return captured;
}
